import { useCallback, useEffect, useMemo, useState } from 'react';
import { useZoomFactor } from '~/hooks';
import { AppCircuitGameNodePoint } from '../AppCircuitGameNodePoint';
import {
	Node,
	getIntersection,
	getPredefinedEdges,
	getRandomNodes,
	isGameSolved,
} from './AppCircuitGameField.model';
import { StyledLine, StyledSvg } from './AppCircuitGameField.styled';

const AppCircuitGameField = ({
	handleGameSolved,
}: {
	handleGameSolved: () => void;
}) => {
	const initialNodes = useMemo(() => getRandomNodes(), []);
	const edges = useMemo(
		() => getPredefinedEdges(initialNodes.length),
		[initialNodes],
	);
	const [nodes, setNodes] = useState<Node[]>(initialNodes);
	const [draggingNode, setDraggingNode] = useState<number | null>(null);
	const [gameSolvedNotificationShown, setGameSolvedNotificationShown] =
		useState(false);
	const zoomFactor = useZoomFactor();

	const gameSolved = useMemo(
		() => isGameSolved(nodes, edges) && !draggingNode,
		[nodes, edges, draggingNode],
	);

	const handleMouseDown = (id: number) => {
		setDraggingNode(id);
	};

	const handleMouseMove = useCallback(
		(event: React.MouseEvent<SVGSVGElement>) => {
			if (draggingNode !== null) {
				const svg = event.currentTarget;
				const minX = 40 * zoomFactor;
				const minY = 40 * zoomFactor;
				const maxX = Number(svg.getAttribute('width')) - 40 * zoomFactor;
				const maxY = Number(svg.getAttribute('height')) - 40 * zoomFactor;

				const rect = svg.getBoundingClientRect();
				let x =
					(event.clientX - rect.left - 15 * zoomFactor) / zoomFactor +
					15 * zoomFactor;
				let y =
					(event.clientY - rect.top - 15 * zoomFactor) / zoomFactor +
					15 * zoomFactor;

				x = Math.max(minX, Math.min(x, maxX));
				y = Math.max(minY, Math.min(y, maxY));

				setNodes((prevNodes) =>
					prevNodes.map((node) =>
						node.id === draggingNode ? { ...node, x, y } : node,
					),
				);
			}
		},
		[draggingNode, zoomFactor],
	);

	const handleMouseUp = () => {
		setDraggingNode(null);
	};

	const intersections: { x: number; y: number }[] = [];
	const intersectingEdges = new Set<number>();

	edges.forEach((edge, index) => {
		const fromNode = nodes.find((n) => n.id === edge.from);
		const toNode = nodes.find((n) => n.id === edge.to);
		if (!fromNode || !toNode) return;

		edges.forEach((otherEdge, otherIndex) => {
			if (index >= otherIndex) return;
			const otherFromNode = nodes.find((n) => n.id === otherEdge.from);
			const otherToNode = nodes.find((n) => n.id === otherEdge.to);
			if (!otherFromNode || !otherToNode) return;

			const intersection = getIntersection(
				fromNode.x,
				fromNode.y,
				toNode.x,
				toNode.y,
				otherFromNode.x,
				otherFromNode.y,
				otherToNode.x,
				otherToNode.y,
			);
			if (intersection) {
				intersections.push(intersection);
				intersectingEdges.add(index);
				intersectingEdges.add(otherIndex);
			}
		});
	});

	useEffect(() => {
		if (gameSolved && !gameSolvedNotificationShown) {
			setGameSolvedNotificationShown(true);
			handleGameSolved();
		}
	}, [gameSolved, gameSolvedNotificationShown, handleGameSolved]);

	return (
		<StyledSvg
			width={1020}
			height={620}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			onMouseLeave={handleMouseUp}
		>
			{edges.map((edge, index) => {
				const fromNode = nodes.find((n) => n.id === edge.from);
				const toNode = nodes.find((n) => n.id === edge.to);
				if (!fromNode || !toNode) return null;

				return (
					<StyledLine
						key={index}
						x1={fromNode.x}
						y1={fromNode.y}
						x2={toNode.x}
						y2={toNode.y}
						$stroke={intersectingEdges.has(index) ? '#D90000' : '#66DB00'}
						strokeWidth={4}
					/>
				);
			})}
			{intersections.map((point, index) => (
				<circle key={index} cx={point.x} cy={point.y} r={5} fill="white" />
			))}
			{nodes.map((node) => (
				<AppCircuitGameNodePoint
					cx={node.x}
					cy={node.y}
					onMouseDown={() => handleMouseDown(node.id)}
					key={node.id}
				/>
			))}
		</StyledSvg>
	);
};

export { AppCircuitGameField };
